import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import * as pdfjs from 'pdfjs-dist';
import {
    FileUp, Sparkles, Loader2, CheckCircle2, 
    ChevronRight, BookOpen, Layers, Calculator, 
    Image as ImageIcon, HelpCircle, Table2, 
    Plus, Trash2, ArrowRight, Library, FileText,
    Settings, Key, X, Check, Brain
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import api from '../../api';

// Reference the worker copied to /public
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const chunkTypeIcons = {
    theory: BookOpen,
    definition: Layers,
    example: ImageIcon,
    solved_problem: Calculator,
    question: HelpCircle,
    image: ImageIcon,
    table: Table2
};

export default function AIPdfParserPanel({ onClose }) {
    const [folders, setFolders] = useState([]);
    const [selectedFolderId, setSelectedFolderId] = useState('');
    const [file, setFile] = useState(null);
    const [extracting, setExtracting] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [parsedData, setParsedData] = useState(null);
    const [extractedText, setExtractedText] = useState('');
    const [mode, setMode] = useState('extraction');
    const [prompt, setPrompt] = useState('');
    const [personalities, setPersonalities] = useState([]);
    const [selectedPersonalityId, setSelectedPersonalityId] = useState('');
    
    // New Settings & Model State
    const [showSettings, setShowSettings] = useState(false);
    const [apiKeys, setApiKeys] = useState({
        groq_api_key: '',
        openai_api_key: '',
        anthropic_api_key: '',
        google_api_key: '',
        xai_api_key: ''
    });
    const [dbModels, setDbModels] = useState([]);
    const [selectedModelId, setSelectedModelId] = useState('');
    const [savingKey, setSavingKey] = useState(false);

    const fileInputRef = useRef(null);

    const fetchTeamSettings = async (teamId) => {
        try {
            const { data, error } = await supabase
                .from('teams')
                .select('api_keys, groq_api_key, openai_api_key, anthropic_api_key, google_api_key, xai_api_key')
                .eq('id', teamId)
                .maybeSingle();
            if (error) throw error;
            if (data) {
                const consolidatedKeys = data.api_keys || {};
                const keys = ['groq_api_key', 'openai_api_key', 'anthropic_api_key', 'google_api_key', 'xai_api_key'];
                keys.forEach(k => {
                    if (data[k] && !consolidatedKeys[k]) consolidatedKeys[k] = data[k];
                });
                setApiKeys({
                    groq_api_key: consolidatedKeys.groq_api_key || '',
                    openai_api_key: consolidatedKeys.openai_api_key || '',
                    anthropic_api_key: consolidatedKeys.anthropic_api_key || '',
                    google_api_key: consolidatedKeys.google_api_key || '',
                    xai_api_key: consolidatedKeys.xai_api_key || ''
                });
            }
        } catch (err) {
            console.error('Failed to fetch team settings:', err);
        }
    };

    const fetchDbModels = async (teamId) => {
        try {
            const res = await api.get(`/api/benchrex/models?team_id=${teamId}`);
            if (res.data) {
                setDbModels(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch models:", err);
        }
    };

    useEffect(() => {
        const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
        const teamId = profile.team_id;
        if (!teamId) return;

        supabase.from('knowledge_folders').select('id, name').eq('team_id', teamId).order('name')
            .then(({ data }) => setFolders(data || []));

        supabase.from('personalities').select('id, name').eq('team_id', teamId).eq('category', 'content-parser').order('name')
            .then(({ data }) => {
                setPersonalities(data || []);
                if (data && data.length > 0) setSelectedPersonalityId(data[0].id);
            });

        fetchTeamSettings(teamId);
        fetchDbModels(teamId);
    }, []);

    const saveApiKey = async (keyName, value) => {
        const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
        const teamId = profile.team_id;
        if (!teamId) return;
        setSavingKey(true);
        try {
            const { error } = await supabase
                .from('teams')
                .update({ 
                    [keyName]: value,
                    api_keys: { ...apiKeys, [keyName]: value }
                })
                .eq('id', teamId);
            if (error) throw error;
            toast.success('API Key saved successfully');
            setApiKeys(prev => ({ ...prev, [keyName]: value }));
        } catch (err) {
            toast.error('Failed to save API key');
            console.error(err);
        } finally {
            setSavingKey(false);
        }
    };

    const handleFileSelect = (e) => {
        const selected = e.target.files[0];
        if (selected && selected.type === 'application/pdf') {
            setFile(selected);
        } else {
            toast.error('Please select a PDF file');
        }
    };

    const extractText = async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            const strings = content.items.map(item => item.str);
            fullText += strings.join(' ') + '\n\n';
        }
        return fullText;
    };

    const runAiParser = async () => {
        if (!file) return;
        setExtracting(true);
        try {
            const text = await extractText(file);
            setExtractedText(text);
            setExtracting(false);
            setParsing(true);

            const token = localStorage.getItem('admin_token');
            const res = await fetch(`${API_BASE}/api/ai/parse-content`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ 
                    text,
                    mode,
                    prompt: prompt.trim() || undefined,
                    personality_id: selectedPersonalityId || undefined,
                    model_id: selectedModelId || undefined
                })
            });

            if (!res.ok) throw new Error('Failed to parse content');
            const data = await res.json();
            setParsedData(data);
            toast.success('Content parsed successfully!');
        } catch (err) {
            toast.error(err.message);
        } finally {
            setExtracting(false);
            setParsing(false);
        }
    };

    const handleImport = async () => {
        if (!parsedData) return;

        setImporting(true);
        try {
            const token = localStorage.getItem('admin_token');
            const res = await fetch(`${API_BASE}/api/content/bulk-import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    parent_folder_id: selectedFolderId || null,
                    chapters: parsedData.chapters
                })
            });

            if (!res.ok) throw new Error('Import failed');
            toast.success('Successfully imported to Knowledge Base!');
            setParsedData(null);
            setFile(null);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="h-full flex flex-col overflow-hidden bg-white dark:bg-zinc-900 shadow-2xl">
            {/* Sidebar Header */}
            <div className="px-5 py-4 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between bg-zinc-50/50 dark:bg-zinc-950/20">
                <div className="flex items-center gap-3">
                    <div className="p-1.5 rounded-lg bg-blue-500 text-white">
                        <Sparkles className="w-4 h-4" />
                    </div>
                    <div>
                        <h3 className="text-sm font-black text-zinc-900 dark:text-zinc-100 uppercase tracking-widest">AI Parser</h3>
                        <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-tight">Content Intelligence</p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => setShowSettings(!showSettings)} className="p-1.5 rounded-md hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-400 transition-colors">
                        <Settings className={cn("w-4 h-4 transition-transform", showSettings && "rotate-90 text-blue-600")} />
                    </button>
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-400 transition-colors">
                        <Plus className="w-4 h-4 rotate-45" />
                    </button>
                </div>
            </div>

            {/* Settings Panel Overlay */}
            {showSettings && (
                <div className="absolute top-[65px] left-0 right-0 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 p-5 z-20 animate-in slide-in-from-top-2 duration-200 shadow-xl max-h-[80%] overflow-y-auto">
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Key className="w-4 h-4 text-blue-600" />
                                <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">API Configuration</span>
                            </div>
                            <button onClick={() => setShowSettings(false)} className="text-zinc-400 hover:text-zinc-600">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {[
                            { name: 'Groq Cloud', key: 'groq_api_key', placeholder: 'gsk_...' },
                            { name: 'OpenAI', key: 'openai_api_key', placeholder: 'sk-...' },
                            { name: 'Anthropic', key: 'anthropic_api_key', placeholder: 'sk-ant-...' },
                            { name: 'Google AI', key: 'google_api_key', placeholder: 'AIza...' },
                            { name: 'xAI (Grok)', key: 'xai_api_key', placeholder: 'xai-...' },
                        ].map((provider) => (
                            <div key={provider.key} className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">{provider.name}</Label>
                                    {apiKeys[provider.key] ? (
                                        <span className="text-[8px] font-bold text-green-500 uppercase tracking-tighter">Configured</span>
                                    ) : (
                                        <span className="text-[8px] font-bold text-zinc-400 uppercase tracking-tighter">Missing</span>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <Input 
                                        type="password" 
                                        placeholder={provider.placeholder} 
                                        value={apiKeys[provider.key]} 
                                        onChange={(e) => setApiKeys(prev => ({ ...prev, [provider.key]: e.target.value }))}
                                        className="h-9 rounded-xl bg-zinc-50 dark:bg-zinc-950 font-mono text-[10px]"
                                    />
                                    <Button size="icon" onClick={() => saveApiKey(provider.key, apiKeys[provider.key])} disabled={savingKey} className="h-9 w-9 shrink-0 bg-blue-600 hover:bg-blue-700 rounded-xl">
                                        {savingKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                    </Button>
                                </div>
                            </div>
                        ))}
                        <p className="text-[10px] text-zinc-500 leading-tight border-t border-zinc-100 dark:border-zinc-800 pt-4">
                            Keys are stored in team settings and shared across all AI tools.
                        </p>
                    </div>
                </div>
            )}

            {/* Model Selection Strip */}
            <div className="px-5 py-2 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/20 flex items-center justify-between">
                <div className="flex items-center gap-1.5 overflow-hidden flex-1">
                    <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400 shrink-0">Model:</span>
                    </div>
                    <select 
                        value={selectedModelId}
                        onChange={(e) => setSelectedModelId(e.target.value)}
                        className="bg-transparent text-[10px] font-bold text-zinc-600 dark:text-zinc-300 outline-none cursor-pointer hover:text-blue-600 transition-colors truncate flex-1"
                    >
                        <option value="">Default (Auto)</option>
                        {dbModels.map(m => (
                            <option key={m.id} value={m.id} className="bg-white dark:bg-zinc-900">{m.name} ({m.provider})</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-6">
                {!parsedData ? (
                    <div className="space-y-6 animate-in fade-in duration-500">
                        {/* Persona Selection */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.15em] text-zinc-400 px-1">AI Analyst Persona</label>
                            <select 
                                value={selectedPersonalityId} 
                                onChange={e => setSelectedPersonalityId(e.target.value)}
                                className="w-full text-xs font-bold rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-4 py-3 focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                            >
                                {personalities.length > 0 ? (
                                    personalities.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))
                                ) : (
                                    <option value="">No Content-Parser Persona Found</option>
                                )}
                            </select>
                        </div>
                        {/* Mode Selection */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.15em] text-zinc-400 px-1">Parsing Mode</label>
                            <select 
                                value={mode} 
                                onChange={e => setMode(e.target.value)}
                                className="w-full text-xs font-bold rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-4 py-3 focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                            >
                                <option value="extraction">Complete Extraction</option>
                                <option value="caption">Image Captionize</option>
                                <option value="summary">Document Summary</option>
                                <option value="qa">Interactive Q&A</option>
                            </select>
                        </div>

                        {/* Custom Prompt */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.15em] text-zinc-400 px-1">Custom Instructions (Optional)</label>
                            <textarea 
                                value={prompt}
                                onChange={e => setPrompt(e.target.value)}
                                placeholder="E.g. 'Extract only the math formulas' or 'Focus on chapter 3'..."
                                className="w-full min-h-[100px] text-xs font-medium rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 px-4 py-3 focus:ring-2 focus:ring-blue-500 transition-all outline-none resize-none"
                            />
                        </div>

                        {/* File Upload */}
                        <div 
                            onClick={() => fileInputRef.current?.click()}
                            className={cn(
                                "group border-2 border-dashed rounded-[32px] p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300",
                                file ? "border-blue-500 bg-blue-50/30 dark:bg-blue-900/10" : "border-zinc-200 dark:border-zinc-800 hover:border-blue-400 hover:bg-zinc-50 dark:hover:bg-zinc-950"
                            )}
                        >
                            <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileSelect} />
                            <div className={cn(
                                "w-12 h-12 rounded-2xl flex items-center justify-center mb-3 transition-transform duration-500 group-hover:scale-110",
                                file ? "bg-blue-500 text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-400"
                            )}>
                                {file ? <CheckCircle2 className="w-6 h-6" /> : <FileUp className="w-6 h-6" />}
                            </div>
                            <p className="text-xs font-bold text-zinc-900 dark:text-zinc-100">{file ? file.name : "Upload PDF Document"}</p>
                            {!file && <p className="text-[9px] text-zinc-500 uppercase mt-1 font-black tracking-widest">Max 20MB</p>}
                        </div>

                        {file && (
                            <div className="space-y-4">
                                {extractedText && (
                                    <div className="px-4 py-3 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-100 dark:border-zinc-800 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <p className="text-[9px] font-black uppercase tracking-widest text-zinc-400">Input Density</p>
                                            <span className={cn(
                                                "text-[9px] font-black px-1.5 py-0.5 rounded uppercase",
                                                extractedText.length > 50000 ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"
                                            )}>
                                                {extractedText.length > 50000 ? "High" : "Optimal"}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-[10px] font-bold">
                                            <span className="text-zinc-500">{extractedText.length.toLocaleString()} Chars</span>
                                            <span className="text-zinc-400">~{Math.round(extractedText.length / 4).toLocaleString()} Tokens</span>
                                        </div>
                                    </div>
                                )}
                                <button
                                    onClick={runAiParser}
                                    disabled={parsing || extracting || !selectedPersonalityId}
                                    className="w-full py-4 rounded-2xl bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 font-black uppercase tracking-widest text-[10px] shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {parsing || extracting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            {extracting ? 'Extracting...' : 'Parsing...'}
                                        </>
                                    ) : (
                                        <>
                                            <ArrowRight className="w-4 h-4" />
                                            Execute AI Pipeline
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6 animate-in slide-in-from-right duration-500">
                        <div className="flex items-center justify-between">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">Analysis Results</h4>
                            <button onClick={() => setParsedData(null)} className="text-[9px] font-black text-red-500 uppercase tracking-widest hover:underline">Reset</button>
                        </div>

                        {/* Import Controls */}
                        <div className="space-y-3 bg-zinc-50 dark:bg-zinc-950 p-4 rounded-[24px] border border-zinc-100 dark:border-zinc-800">
                            <select 
                                value={selectedFolderId} 
                                onChange={e => setSelectedFolderId(e.target.value)}
                                className="w-full text-[11px] font-bold rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-3 py-2.5 outline-none"
                            >
                                <option value="">Target Folder: Root</option>
                                {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                            </select>
                            <button
                                onClick={handleImport}
                                disabled={importing}
                                className="w-full py-3 rounded-xl bg-blue-600 text-white font-black uppercase tracking-widest text-[10px] shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
                            >
                                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                Commit to Base
                            </button>
                        </div>

                        {/* Result Content */}
                        <div className="space-y-4">
                            {parsedData.chapters.map((chapter, idx) => (
                                <div key={idx} className="space-y-2">
                                    <div className="flex items-center gap-2 px-1">
                                        <div className="w-1 h-1 rounded-full bg-blue-500" />
                                        <p className="text-[11px] font-bold text-zinc-900 dark:text-zinc-100 truncate">{chapter.name}</p>
                                    </div>
                                    <div className="space-y-2">
                                        {chapter.chunks.map((chunk, cidx) => {
                                            const Icon = chunkTypeIcons[chunk.type] || FileText;
                                            return (
                                                <div key={cidx} className="p-3 rounded-xl bg-white dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700">
                                                    <div className="flex items-start gap-3">
                                                        <Icon className="w-3.5 h-3.5 text-zinc-400 mt-0.5 shrink-0" />
                                                        <div className="min-w-0">
                                                            <p className="text-[10px] font-bold text-zinc-700 dark:text-zinc-300 leading-tight mb-1">{chunk.name}</p>
                                                            <p className="text-[9px] text-zinc-500 line-clamp-2 italic">{chunk.content}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
